// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { ApiService, BaseApiService } from '@fc-core/services/base-api.service';
import { HttpClient, HttpParams } from '@angular/common/http';

import { MomentService } from '@fc-core/services/moment.service';
import { FCHttpParamEncoder } from '@fc-shared/utils/param.encoder';
import { map } from 'rxjs/operators';

export interface StatsQueryData {
  url: string;
  params: HttpParams;
}

export interface VehicleStats {
  id: string;
  vin: string;
  realOdometer?: any;
  realEngineHours?: any;
  fuelLevel: number;
  vehicleVoltage: number;
}

export interface VehicleEcu {
  id: string;
  odometer?: any;
  engineHours: number;
}

export interface VehicleFuelLevel {
  latest: { date: Date; value: number };
  results: { date: Date; fuelLevel: number }[];
}

export interface ReportsDateRange {
  rangeLower: string;
  rangeUpper: string;
}

export interface VehicleReportRangePoint {
  date: Date;
  min: number;
  avg: number;
  max: number;
}

export type PowerSourceType = 'vehicle' | 'solar' | 'battery';

export const SOURCE_VOLTAGE_LABELS: Record<PowerSourceType, string> = {
  vehicle: 'Vehicle Voltage',
  solar: 'Solar Voltage',
  battery: 'Battery Voltage',
};

export interface VehicleSourceVoltage {
  latest: { date: Date; value: number };
  results: { date: Date; voltage: number }[];
  source: PowerSourceType;
  threshold: number | null;
}

export interface VehicleSourceVoltageRange {
  latest: VehicleReportRangePoint;
  results: VehicleReportRangePoint[];
  source: PowerSourceType;
  threshold: number | null;
}

/** @deprecated The endpoint was renamed to `source-voltage`; use {@link VehicleSourceVoltage}. */
export type VehicleBatteryLevel = VehicleSourceVoltage;

export interface VehicleBatteryVoltage {
  latest: { date: Date; value: number };
  results: { date: Date; batteryVolt: number }[];
  threshold: number | null;
}

export interface VehicleBatteryVoltageRange {
  latest: VehicleReportRangePoint;
  results: VehicleReportRangePoint[];
  threshold: number | null;
}

export interface VehicleBatteryPercent {
  latest: { date: Date; value: number };
  results: { date: Date; batteryPercent: number }[];
  threshold: number | null;
}

export interface VehicleStatistic {
  name: string;
  data: {
    date: Date;
    day: string;
    current: number;
    previous: number;
  }[];
}

@Injectable({ providedIn: 'root' })
@ApiService({})
export class VehicleStatisticsApiService extends BaseApiService {
  constructor(
    private http: HttpClient,
    private momentService: MomentService,
  ) {
    super();
  }

  fromJSON(res: any) {
    if (!res.results) {
      return [];
    }

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return res.results.map((item: any) => {
      const moment_date = this.momentService.moment(
        item.date,
        null,
        null,
        true,
      );
      item.date = moment_date.toDate();
      item.day = days[moment_date.day()];
      return item;
    });
  }

  getPrevious(vehicleId: number, category: string): Observable<any> {
    const queryData = this.previous(vehicleId, category);
    return this.http.get(queryData.url, { params: queryData.params }).pipe(
      map((res) => this.fromJSON(res)),
      map((res: any) => this.formatData(res, category, true)),
    );
  }

  getCurrent(vehicleId: number, category: string): Observable<any> {
    const queryData = this.current(vehicleId, category);
    return this.http.get(queryData.url, { params: queryData.params }).pipe(
      map((res) => this.fromJSON(res)),
      map((res: any) => this.formatData(res, category, false)),
    );
  }

  formatData(array: any, category: string, previous: boolean) {
    switch (category) {
      case 'mileage':
        return array.map((item: any) => {
          if (previous) {
            item.previous = item.traveled;
          } else {
            item.current = item.traveled;
          }
          delete item.traveled;
          return item;
        });
      case 'hours':
        return array.map((item: any) => {
          if (previous) {
            item.previous = item.hours;
          } else {
            item.current = item.hours;
          }
          delete item.hours;
          return item;
        });
      default:
        return array;
    }
  }

  getCompare(
    vehicleId: number,
    category: string,
  ): Observable<VehicleStatistic[]> {
    const previous = this.getPrevious(vehicleId, category);
    const current = this.getCurrent(vehicleId, category);

    return forkJoin([previous, current]).pipe(
      map((res) => {
        const dataset = [];

        dataset.push({
          name: 'Current',
          data: res[1].map((item: any) => {
            return { ...item, previous: 0 };
          }),
        });

        dataset.push({
          name: 'Previous',
          data: res[0].map((item: any) => {
            return { ...item, current: item.previous, previous: 0 };
          }),
        });

        dataset.push({
          name: 'Compare',
          data: this.compare(res[0], res[1]),
        });

        return dataset;
      }),
    );
  }

  getBatteryLevel(
    vehicleId: number,
    range: ReportsDateRange,
  ): Observable<VehicleSourceVoltageRange>;
  getBatteryLevel(vehicleId: number): Observable<VehicleSourceVoltage>;
  getBatteryLevel(
    vehicleId: number,
    range?: ReportsDateRange,
  ): Observable<VehicleSourceVoltage | VehicleSourceVoltageRange> {
    return this.http.get<VehicleSourceVoltage | VehicleSourceVoltageRange>(
      `${this.getBaseUrl()}/vehicles/${vehicleId}/reports/source-voltage/`,
      { params: this.reportsRangeParams(range) },
    );
  }

  getBatteryVoltage(
    vehicleId: number,
    range: ReportsDateRange,
  ): Observable<VehicleBatteryVoltageRange>;
  getBatteryVoltage(vehicleId: number): Observable<VehicleBatteryVoltage>;
  getBatteryVoltage(
    vehicleId: number,
    range?: ReportsDateRange,
  ): Observable<VehicleBatteryVoltage | VehicleBatteryVoltageRange> {
    return this.http.get<VehicleBatteryVoltage | VehicleBatteryVoltageRange>(
      `${this.getBaseUrl()}/vehicles/${vehicleId}/reports/battery-voltage/`,
      { params: this.reportsRangeParams(range) },
    );
  }

  getBatteryPercent(
    vehicleId: number,
    range?: ReportsDateRange,
  ): Observable<VehicleBatteryPercent> {
    return this.http.get<VehicleBatteryPercent>(
      `${this.getBaseUrl()}/vehicles/${vehicleId}/reports/battery-percent/`,
      { params: this.reportsRangeParams(range) },
    );
  }

  getFuelLevel(
    vehicleId: number,
    range?: ReportsDateRange,
  ): Observable<VehicleFuelLevel> {
    return this.http.get<VehicleFuelLevel>(
      `${this.getBaseUrl()}/vehicles/${vehicleId}/reports/fuel-levels/`,
      { params: this.reportsRangeParams(range) },
    );
  }

  private reportsRangeParams(range?: ReportsDateRange): HttpParams {
    let params = new HttpParams({ encoder: new FCHttpParamEncoder() });
    if (range) {
      params = params
        .set('duration__range_lower', range.rangeLower)
        .set('duration__range_upper', range.rangeUpper);
    }
    return params;
  }

  getVehicleStats(vehicleId: number): Observable<VehicleStats> {
    return this.http.get<VehicleStats>(
      `${this.getBaseUrl()}/vehicles/${vehicleId}/stats/`,
    );
  }

  getVehicleEcu(vehicleId: number): Observable<VehicleEcu> {
    return this.http.get<VehicleEcu>(
      `${this.getBaseUrl()}/vehicles/${vehicleId}/ecu/`,
    );
  }

  setVehicleStats(
    vehicleId: number,
    body: { realEngineHours: number; realOdometer: number },
  ): Observable<VehicleStats> {
    return this.http.put<VehicleStats>(
      `${this.getBaseUrl()}/vehicles/${vehicleId}/stats/`,
      body,
    );
  }

  protected compare(previous: any, current: any) {
    if (previous.length >= current.length) {
      return previous.map((prev_item: any) => {
        const find = current.find(
          (curr_item: any) => curr_item.day === prev_item.day,
        );

        prev_item.current = find ? find.current : 0;

        return prev_item;
      });
    } else {
      return current.map((curr_item: any) => {
        const find = previous.find(
          (prev_item: any) => prev_item.day === curr_item.day,
        );

        curr_item.previous = find ? find.previous : 0;

        return curr_item;
      });
    }
  }

  protected previous(vehicleId: number, category: string): StatsQueryData {
    const start = this.momentService.moment().startOf('week').add(-1, 'week');
    const end = start.clone().endOf('week');
    const params = new HttpParams({ encoder: new FCHttpParamEncoder() })
      .append('time_frame', 'daily')
      .append('start', start.format())
      .append('end', end.format());
    return {
      url: this.getStatisticsUrl(vehicleId, category),
      params: params,
    };
  }

  protected current(vehicleId: number, category: string): StatsQueryData {
    const start = this.momentService.moment().startOf('week');
    const end = start.clone().endOf('week');
    const params = new HttpParams({ encoder: new FCHttpParamEncoder() })
      .append('time_frame', 'daily')
      .append('start', start.format())
      .append('end', end.format());
    return {
      url: this.getStatisticsUrl(vehicleId, category),
      params: params,
    };
  }

  protected getStatisticsUrl(vehicleId: number, category: string): string {
    return `${this.getApiUrl()}api/reports/vehicle/${vehicleId}/${category}/`;
  }
}
